import { appConfig } from 'appConfig';
import Icon from 'components/Icon';
import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import Button from 'components/Button';
import { useAutoCreateEstimateOptions } from 'queries/job';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { actionLabel } from 'utils/misc';
import { useQueryClient } from '@tanstack/react-query';
import {
  createEstimateFromMeasurement,
  createLightEstimateFromMeasurement,
} from 'queries/estimates';
import {
  createMatorderFromMeasurements,
  createLightMatorderFromMeasurements,
} from 'queries/matorders';
import {
  createWorkorderFromMeasurements,
  createLightWorkorderFromMeasurements,
} from 'queries/workorders';
import { notifyJobStatsRefresh, notifyExpenseCreate } from 'utils/iframe';
import useAuth from 'hooks/useAuth';
import EstimateCreationProgress, {
  MEASUREMENT_STEPS,
  StepProgressCallback,
} from '../EstimateCreationProgress';
import EstimateAccordion from '../EstimateAccordion';
import LegacyForm, { IFormPayload, LegacyFormRef } from './LegacyForm';
import AddEstimateButton from '../AddEstimateButton';

type EstimateCardProps = {
  estimateName: string;
  isOpen: boolean;
  onToggle: () => void;
  onRemove: () => void;
  canRemove: boolean;
  jobId: string;
  initialRep?: number;
  initialBidType?: string;
  showRepField: boolean;
  showBidTypeField: boolean;
  sharedRep?: number;
  sharedBidType?: string;
  onRepSelect: (rep: number) => void;
  onBidTypeSelect: (bidType: string) => void;
  onFormChange: () => void;
};

type AppProps = {
  goBack?: () => void;
  closeModal?: () => void;
  redirectTo?: 'estimates' | 'estimate-detail';
};

// EstimateCard Component for Measurements
const EstimateCard = React.forwardRef<
  {
    getFormData: () => Promise<IFormPayload | null>;
    validate: () => boolean;
    checkValidity: () => boolean;
  },
  EstimateCardProps
>(
  (
    {
      estimateName,
      isOpen,
      onToggle,
      onRemove,
      canRemove,
      jobId,
      initialRep,
      initialBidType,
      showRepField,
      showBidTypeField,
      sharedRep,
      sharedBidType,
      onRepSelect,
      onBidTypeSelect,
      onFormChange,
    },
    ref
  ) => {
    const formRef = useRef<LegacyFormRef>(null);

    useImperativeHandle(ref, () => ({
      getFormData: async () => {
        return formRef.current?.getFormData() || null;
      },
      validate: () => {
        return formRef.current?.validate() ?? false;
      },
      checkValidity: () => {
        return formRef.current?.checkValidity() ?? false;
      },
    }));

    return (
      <EstimateAccordion
        title={estimateName}
        isOpen={isOpen}
        onToggle={onToggle}
        onRemove={onRemove}
        canRemove={canRemove}
      >
        <form onSubmit={(e) => e.preventDefault()}>
          <LegacyForm
            ref={formRef}
            jobId={jobId}
            showRepField={showRepField}
            showBidTypeField={showBidTypeField}
            initialRep={initialRep}
            initialBidType={initialBidType}
            sharedRep={sharedRep}
            sharedBidType={sharedBidType}
            onRepSelect={onRepSelect}
            onBidTypeSelect={onBidTypeSelect}
            onFormChange={onFormChange}
          />
        </form>
      </EstimateAccordion>
    );
  }
);

EstimateCard.displayName = 'EstimateCard';

const AutomaticCreate: React.FC<AppProps> = ({
  goBack,
  closeModal,
  redirectTo = 'estimate-detail',
}) => {
  const { jobId = '' } = useParams();
  const navigate = useNavigate();
  const { embedMode, userInfo } = useAuth();
  const {
    data: autoCreateEstimateOptions,
    isLoading,
    refetch: refetchAutoCreateOptions,
  } = useAutoCreateEstimateOptions(jobId);

  const newStartIndex = autoCreateEstimateOptions?.new_start_index ?? 1;
  const totalEstimates = autoCreateEstimateOptions?.total_estimates ?? 0;
  const maxEstimates = 6 - totalEstimates;

  // Refetch options when component mounts to ensure we have latest data
  useEffect(() => {
    refetchAutoCreateOptions();
  }, [refetchAutoCreateOptions]);

  const [estimates, setEstimates] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [openEstimateId, setOpenEstimateId] = useState<number | null>(null);
  const [nextId, setNextId] = useState(1);
  const initializedRef = useRef(false);
  const estimateFormRefs = useRef<
    Record<
      number,
      {
        getFormData: () => Promise<IFormPayload | null>;
        validate: () => boolean;
        checkValidity: () => boolean;
      }
    >
  >({});
  const [allFormsValid, setAllFormsValid] = useState(false);
  const [selectedRep, setSelectedRep] = useState<number | undefined>(undefined);
  const [selectedBidType, setSelectedBidType] = useState<string | undefined>(
    undefined
  );
  const [repSelectedInEstimateId, setRepSelectedInEstimateId] = useState<
    number | null
  >(null);
  const [bidTypeSelectedInEstimateId, setBidTypeSelectedInEstimateId] =
    useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [estimatesToCreate, setEstimatesToCreate] = useState<
    Array<{ id: number; name: string; formData: IFormPayload }>
  >([]);

  const queryClient = useQueryClient();

  // Initialize selected values from autoCreateEstimateOptions
  useEffect(() => {
    if (autoCreateEstimateOptions) {
      if (autoCreateEstimateOptions.rep) {
        setSelectedRep(autoCreateEstimateOptions.rep);
      }
      if (autoCreateEstimateOptions.bid_type) {
        setSelectedBidType(autoCreateEstimateOptions.bid_type);
      }
    }
  }, [autoCreateEstimateOptions]);

  // Initialize first estimate when data is loaded
  useEffect(() => {
    if (
      !isLoading &&
      autoCreateEstimateOptions &&
      !initializedRef.current &&
      estimates.length === 0 &&
      maxEstimates > 0
    ) {
      const firstEstimate = {
        id: 1,
        name: `Estimate_${newStartIndex}`,
      };
      setEstimates([firstEstimate]);
      setOpenEstimateId(1);
      setNextId(2);
      initializedRef.current = true;
    }
  }, [
    isLoading,
    autoCreateEstimateOptions,
    newStartIndex,
    estimates.length,
    maxEstimates,
  ]);

  // Check validity of all forms without setting errors (for button disabled state)
  const checkAllFormsValidity = (): boolean => {
    for (const estimate of estimates) {
      const formRef = estimateFormRefs.current[estimate.id];
      if (formRef) {
        if (!formRef.checkValidity()) {
          return false;
        }
      }
    }
    return estimates.length > 0;
  };

  // Update validity state when forms change
  const handleFormChange = () => {
    setAllFormsValid(checkAllFormsValidity());
  };

  // Validate all estimate forms and return whether all are valid (sets errors)
  const validateAllEstimates = (): boolean => {
    let allValid = true;
    for (const estimate of estimates) {
      const formRef = estimateFormRefs.current[estimate.id];
      if (formRef) {
        const isValid = formRef.validate();
        if (!isValid) {
          allValid = false;
        }
      }
    }
    return allValid;
  };

  const handleAddEstimate = () => {
    if (estimates.length < maxEstimates) {
      // Validate existing estimates before adding a new one
      const allValid = validateAllEstimates();
      if (!allValid) {
        toast.error(
          'Please fill in all required fields before adding another estimate'
        );
        return;
      }

      const newEstimate = {
        id: nextId,
        name: `Estimate_${newStartIndex + estimates.length}`,
      };
      setEstimates([...estimates, newEstimate]);
      setOpenEstimateId(newEstimate.id);
      setNextId(nextId + 1);
    }
  };

  const handleRemoveEstimate = (id: number) => {
    const updatedEstimates = estimates
      .filter((est) => est.id !== id)
      .map((est, index) => ({
        ...est,
        name: `Estimate_${newStartIndex + index}`,
      }));

    setEstimates(updatedEstimates);

    if (openEstimateId === id) {
      if (updatedEstimates.length > 0) {
        setOpenEstimateId(updatedEstimates[0].id);
      } else {
        setOpenEstimateId(null);
      }
    }
  };

  const handleToggleEstimate = (id: number) => {
    setOpenEstimateId(openEstimateId === id ? null : id);
  };

  const handleRepSelect = (rep: number, estimateId: number) => {
    setSelectedRep(rep);
    setRepSelectedInEstimateId(estimateId);
  };

  const handleBidTypeSelect = (bidType: string, estimateId: number) => {
    setSelectedBidType(bidType);
    setBidTypeSelectedInEstimateId(estimateId);
  };

  // Determine if rep/bid_type fields should be shown
  const shouldShowRepField = (estimateId: number) => {
    const hasInitialRep = autoCreateEstimateOptions?.rep;
    if (hasInitialRep) return false;
    return !repSelectedInEstimateId || repSelectedInEstimateId === estimateId;
  };

  const shouldShowBidTypeField = (estimateId: number) => {
    const hasInitialBidType = autoCreateEstimateOptions?.bid_type;
    if (hasInitialBidType) return false;
    return (
      !bidTypeSelectedInEstimateId || bidTypeSelectedInEstimateId === estimateId
    );
  };

  const handleCreateEstimates = async () => {
    // Validate all forms first to show errors
    const allValid = validateAllEstimates();
    if (!allValid) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Collect all form data
    const allFormData: Array<{
      id: number;
      name: string;
      formData: IFormPayload;
    }> = [];

    for (const estimate of estimates) {
      const formRef = estimateFormRefs.current[estimate.id];

      if (formRef) {
        const formData = await formRef.getFormData();

        if (formData) {
          allFormData.push({
            id: estimate.id,
            name: estimate.name,
            formData,
          });
        } else {
          toast.error(
            `Please fill in all required fields for ${estimate.name}`
          );
          return;
        }
      } else {
        toast.error(`Form not initialized for ${estimate.name}`);
        return;
      }
    }

    if (allFormData.length === 0) {
      toast.error('No valid estimates to create');
      return;
    }

    // Set state and show progress
    setEstimatesToCreate(allFormData);
    setIsCreating(true);
  };

  // Create estimate and orders (3 API calls per estimate)
  const handleCreateEstimateAndOrders = async (
    formData: IFormPayload,
    onStepProgress?: StepProgressCallback
  ): Promise<{ id: number }> => {
    let estimateRes: any;
    const isV2 = formData.version === 'v2';

    // Step 1: Create Estimate
    onStepProgress?.('estimate', 'in_progress');
    try {
      estimateRes = isV2
        ? await createLightEstimateFromMeasurement(jobId, formData)
        : await createEstimateFromMeasurement(jobId, formData);
      onStepProgress?.('estimate', 'created');
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create estimate';
      onStepProgress?.('estimate', 'failed', errorMessage);
      throw err;
    }

    // Step 2: Create Matorder
    onStepProgress?.('matorder', 'in_progress');
    try {
      if (isV2) {
        await createLightMatorderFromMeasurements(jobId, {
          ...formData,
          estimate: estimateRes?.id,
        });
      } else {
        await createMatorderFromMeasurements(jobId, {
          ...formData,
          estimate: estimateRes?.id,
        });
      }
      onStepProgress?.('matorder', 'created');
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create matorder';
      onStepProgress?.('matorder', 'failed', errorMessage);
      throw err;
    }

    // Step 3: Create Workorder
    onStepProgress?.('workorder', 'in_progress');
    try {
      if (isV2) {
        await createLightWorkorderFromMeasurements(jobId, {
          ...formData,
          estimate: estimateRes?.id,
        });
      } else {
        await createWorkorderFromMeasurements(jobId, {
          ...formData,
          estimate: estimateRes?.id,
        });
      }
      onStepProgress?.('workorder', 'created');
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create workorder';
      onStepProgress?.('workorder', 'failed', errorMessage);
      throw err;
    }

    return estimateRes;
  };

  const handleCreationComplete = (createdIds: number[]) => {
    // Invalidate queries
    queryClient.invalidateQueries(['estimates']);
    queryClient.invalidateQueries(['job-detail', jobId]);
    queryClient.invalidateQueries(['job-stats', jobId]);
    queryClient.invalidateQueries(['job-workorders', { job: jobId }]);
    queryClient.invalidateQueries(['job-matorders', { job: jobId }]);
    queryClient.invalidateQueries(['auto-create-estimate-options', jobId]);

    // Notify parent window if in embed mode
    if (embedMode) {
      notifyJobStatsRefresh(jobId);
      notifyExpenseCreate(jobId);
    }

    // Redirect
    if (embedMode && window.top) {
      if (createdIds.length > 1) {
        window.top.location.replace(
          `${appConfig.legacyUrl}/jobs/${jobId}/estimateOptions`
        );
      } else {
        window.top.location.replace(
          `${appConfig.legacyUrl}/jobs/${jobId}/estimates/${createdIds[0]}`
        );
      }
    } else {
      closeModal?.();
      navigate(`/jobs/${jobId}/estimates`, { replace: true });
    }
  };

  const handleAllFailed = () => {
    queryClient.invalidateQueries(['estimates']);
    queryClient.invalidateQueries(['job-detail', jobId]);
    queryClient.invalidateQueries(['job-stats', jobId]);
    queryClient.invalidateQueries(['auto-create-estimate-options', jobId]);
    setIsCreating(false);
    setEstimatesToCreate([]);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Show creation progress
  if (isCreating && estimatesToCreate.length > 0) {
    return (
      <EstimateCreationProgress
        estimates={estimatesToCreate}
        createEstimate={handleCreateEstimateAndOrders}
        onComplete={handleCreationComplete}
        onAllFailed={handleAllFailed}
        steps={MEASUREMENT_STEPS}
        flow="measurement"
      />
    );
  }

  return (
    <div className="pb-4">
      <div className="flex justify-between mb-6">
        <div className="text-lg font-bold">
          Create Estimate from Measurements
        </div>
        {!!closeModal && (
          <div>
            <Icon name="close" size={24} onClick={closeModal} />
          </div>
        )}
      </div>

      <div className="space-y-3 mb-4">
        {estimates.map((estimate) => {
          const isOpen = openEstimateId === estimate.id;
          return (
            <EstimateCard
              key={estimate.id}
              ref={(ref) => {
                if (ref) {
                  estimateFormRefs.current[estimate.id] = ref;
                } else if (!estimates.find((e) => e.id === estimate.id)) {
                  delete estimateFormRefs.current[estimate.id];
                }
              }}
              estimateName={estimate.name}
              isOpen={isOpen}
              onToggle={() => handleToggleEstimate(estimate.id)}
              onRemove={() => handleRemoveEstimate(estimate.id)}
              canRemove={estimates.length > 1}
              jobId={jobId}
              initialRep={autoCreateEstimateOptions?.rep}
              initialBidType={autoCreateEstimateOptions?.bid_type}
              showRepField={shouldShowRepField(estimate.id)}
              showBidTypeField={shouldShowBidTypeField(estimate.id)}
              sharedRep={selectedRep}
              sharedBidType={selectedBidType}
              onRepSelect={(rep) => handleRepSelect(rep, estimate.id)}
              onBidTypeSelect={(bidType) =>
                handleBidTypeSelect(bidType, estimate.id)
              }
              onFormChange={handleFormChange}
            />
          );
        })}
      </div>

      {estimates.length < maxEstimates && (
        <AddEstimateButton
          guideKey="CREATE_FROM_MEASUREMENT"
          onAdd={handleAddEstimate}
        />
      )}

      <Button
        label={actionLabel(null, 'Estimates', isCreating)}
        className="mt-8"
        loading={isCreating}
        onClick={handleCreateEstimates}
        disabled={isCreating || !allFormsValid}
      />

      {!isCreating && (
        <div className="center mt-4 cursor-pointer" onClick={goBack}>
          <Icon name="arrow-left" size={16} />
          <div className="text-sm text-text ml-2">Select another option</div>
        </div>
      )}
    </div>
  );
};

export default AutomaticCreate;
